import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ChevronDown, ChevronRight, Wand2, Upload } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { RichText } from "@/components/RichText";
import { buildCombinationRows, validateGroupedCombination } from "../groupedCombination";
import { extractImportedLists, extractImportedMapping, mergeResultMapping } from "../resultMappingImport";

/**
 * Track B — Universal Result Mapping / Layout / Resolver editors.
 *
 * These three editors together replace the old "template_type + severity_levels + hero_config"
 * flow with a single generic engine. Old fields are still preserved on the instrument document
 * for backward compatibility, but the public result page prefers the new fields.
 */

// ---------- Result Mapping Editor ----------
export function ResultMappingEditor({ mapping, onChange, dimensions, method, combinedDims, severityLevels, resolver, lists, onListsChange, lang }) {
  const upd = (i, patch) => onChange(mapping.map((m, k) => (k === i ? { ...m, ...patch } : m)));
  const rm = (i) => onChange(mapping.filter((_, k) => k !== i));
  const add = (key = "") => onChange([
    ...mapping,
    {
      id: cryptoRandomId(),
      result_key: key,
      title_id: "", title_en: "",
      subtitle_id: "", subtitle_en: "",
      reflection_id: "", reflection_en: "",
      hero_image_id_url: "", hero_image_en_url: "",
      share_image_id_url: "", share_image_en_url: "",
      color: "", badge_id: "", badge_en: "",
      pdf_template_id: null, extras: {},
    },
  ]);

  const fileRef = React.useRef(null);
  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      const imported = extractImportedMapping(data);
      const { mapping: next, added, updated } = mergeResultMapping(mapping, imported, cryptoRandomId);
      onChange(next);
      // A file that also defines Learn More headings replaces the test's list headings.
      const importedLists = extractImportedLists(data);
      if (importedLists && onListsChange) onListsChange(importedLists);
      toast.success(lang === "id"
        ? `${added} hasil ditambahkan, ${updated} diperbarui. Klik Simpan untuk menyimpan.`
        : `${added} results added, ${updated} updated. Click Save to keep them.`);
    } catch {
      toast.error(lang === "id" ? "File JSON tidak berisi Result Mapping yang valid." : "The JSON file has no valid Result Mapping.");
    }
  };

  const suggestedKeys = resolver?.mode === "grouped_combination"
    ? (resolver.combination_rules || []).map((rule) => rule.result_key).filter(Boolean)
    : resolver?.mode === "holland_code" && method === "continuous_multi"
      ? getHollandSuggestedKeys(dimensions)
      : resolver?.mode === "big_five" && method === "continuous_multi"
        ? getBigFiveSuggestedKeys(dimensions)
        : resolver?.mode === "type_code" && method === "continuous_multi"
          ? getTypeCodeSuggestedKeys(dimensions)
          : getSuggestedKeys({ method, dimensions, combinedDims, severityLevels });

  return (
    <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="result-mapping-block">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg">{lang === "id" ? "Result Mapping (Hero Card per Hasil)" : "Result Mapping (Hero Card per Result)"}</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            {lang === "id"
              ? "Untuk setiap kemungkinan hasil (mis. Moderate, Physical Touch, RIA), unggah Hero Card, tulis judul dan refleksi. Halaman hasil publik akan menampilkan mapping ini secara otomatis."
              : "For each possible result (e.g. Moderate, Physical Touch, RIA), upload a Hero Card and write the title + reflection. The public result page uses these mappings automatically."}
          </p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".json,application/json" onChange={importFile} className="hidden" data-testid="rmap-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full" data-testid="rmap-import">
            <Upload className="w-4 h-4 mr-1" /> {lang === "id" ? "Impor JSON" : "Import JSON"}
          </Button>
          <Button variant="outline" onClick={() => add("")} className="rounded-full" data-testid="rmap-add">
            <Plus className="w-4 h-4 mr-1" /> {lang === "id" ? "Tambah" : "Add"}
          </Button>
        </div>
      </div>

      {suggestedKeys.length > 0 && (
        <div className="p-3 rounded-xl border border-sage-200 bg-sage-50/60 text-xs">
          <div className="text-muted-foreground mb-2 flex items-center gap-1.5">
            <Wand2 className="w-3.5 h-3.5" />
            {lang === "id" ? "Sarankan key dari config sekarang:" : "Suggested keys from current config:"}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="rmap-suggestions">
            {suggestedKeys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => add(k)}
                className="px-2.5 py-1 rounded-full bg-sage-100 border border-sage-200 hover:bg-sage-200 font-mono text-[11px] text-teal-800"
                data-testid={`rmap-suggest-${k}`}
              >+ {k}</button>
            ))}
          </div>
        </div>
      )}

      {mapping.length === 0 && (
        <div className="text-xs text-muted-foreground italic">
          {lang === "id" ? "Belum ada mapping. Klik “Tambah” atau pakai saran di atas." : "No mappings yet. Click “Add” or use a suggestion above."}
        </div>
      )}

      <LearnMoreListsEditor lists={lists} onChange={onListsChange} lang={lang} />

      <div className="space-y-3">
        {mapping.map((m, i) => (
          <MappingRow key={m.id || i} index={i} entry={m} lists={lists} upd={(patch) => upd(i, patch)} rm={() => rm(i)} lang={lang} />
        ))}
      </div>
    </div>
  );
}


function MappingRow({ index, entry, lists, upd, rm, lang }) {
  const [open, setOpen] = React.useState(index === 0);
  return (
    <div className="rounded-xl border border-border bg-secondary/30" data-testid={`rmap-row-${index}`}>
      <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-terracotta-100 text-terracotta-700 shrink-0">{entry.result_key || "(no key)"}</span>
        <span className="text-sm text-teal-800 font-display truncate">{entry[`title_${lang}`] || entry.title_id || entry.title_en || (lang === "id" ? "(belum diisi)" : "(untitled)")}</span>
        <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {entry.color && <span className="w-4 h-4 rounded-full border border-border" style={{ background: entry.color }} />}
          <Button size="icon" variant="ghost" onClick={rm} className="text-destructive h-7 w-7" data-testid={`rmap-rm-${index}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      {open && (
        <div className="p-4 border-t border-border space-y-4">
          {/* Preview panel — hero + share side-by-side, matches what respondents see */}
          <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-background border border-dashed border-border" data-testid={`rmap-preview-${index}`}>
            {[
              { key: "hero", url_id: entry.hero_image_id_url, url_en: entry.hero_image_en_url, label_id: "Hero Card", label_en: "Hero Card" },
              { key: "share", url_id: entry.share_image_id_url, url_en: entry.share_image_en_url, label_id: "Share Image", label_en: "Share Image" },
            ].map((p) => (
              <div key={p.key} className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">{lang === "id" ? p.label_id : p.label_en}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {["id", "en"].map((lg) => {
                    const url = p[`url_${lg}`];
                    return (
                      <div key={lg} className="relative aspect-square rounded-md bg-secondary/50 border border-border overflow-hidden">
                        <span className="absolute top-0.5 left-0.5 z-10 text-[9px] font-mono bg-white/85 px-1 rounded">{lg.toUpperCase()}</span>
                        {url ? (
                          <img src={url} alt={`${p.key} ${lg}`} className="w-full h-full object-contain" data-testid={`rmap-preview-${p.key}-${lg}-${index}`} />
                        ) : (
                          <span className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Result Key" : "Result Key"}</Label>
              <Input value={entry.result_key || ""} onChange={(e) => upd({ result_key: e.target.value })} className="font-mono text-xs" placeholder={lang === "id" ? "mis: moderate, words, RIA" : "e.g. moderate, words, RIA"} data-testid={`rmap-key-${index}`} />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Warna" : "Color"}</Label>
              <Input type="color" value={entry.color || "#c46b4f"} onChange={(e) => upd({ color: e.target.value })} className="h-10" data-testid={`rmap-color-${index}`} />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Badge (ID)" : "Badge (ID)"}</Label>
              <Input value={entry.badge_id || ""} onChange={(e) => upd({ badge_id: e.target.value })} placeholder={lang === "id" ? "mis: BURNOUT SEDANG" : "e.g. MODERATE"} data-testid={`rmap-badge-id-${index}`} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Judul (ID)" : "Title (ID)"}</Label>
              <Input value={entry.title_id || ""} onChange={(e) => upd({ title_id: e.target.value })} data-testid={`rmap-title-id-${index}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Judul (EN)" : "Title (EN)"}</Label>
              <Input value={entry.title_en || ""} onChange={(e) => upd({ title_en: e.target.value })} data-testid={`rmap-title-en-${index}`} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Subjudul (ID)" : "Subtitle (ID)"}</Label>
              <Input value={entry.subtitle_id || ""} onChange={(e) => upd({ subtitle_id: e.target.value })} data-testid={`rmap-sub-id-${index}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Subjudul (EN)" : "Subtitle (EN)"}</Label>
              <Input value={entry.subtitle_en || ""} onChange={(e) => upd({ subtitle_en: e.target.value })} data-testid={`rmap-sub-en-${index}`} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Refleksi (ID)" : "Reflection (ID)"}</Label>
              <div className="mt-1"><RichText value={entry.reflection_id || ""} onChange={(v) => upd({ reflection_id: v })} testid={`rmap-refl-id-${index}`} /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Refleksi (EN)" : "Reflection (EN)"}</Label>
              <div className="mt-1"><RichText value={entry.reflection_en || ""} onChange={(v) => upd({ reflection_en: v })} testid={`rmap-refl-en-${index}`} /></div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Hero Card (ID)</Label>
              <ImageUpload value={entry.hero_image_id_url || ""} onChange={(url) => upd({ hero_image_id_url: url })} placeholder="Hero (ID)" testid={`rmap-hero-id-${index}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Hero Card (EN)</Label>
              <ImageUpload value={entry.hero_image_en_url || ""} onChange={(url) => upd({ hero_image_en_url: url })} placeholder="Hero (EN)" testid={`rmap-hero-en-${index}`} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                Share Image (ID) <span className="text-[10px] text-muted-foreground normal-case tracking-normal">— {lang === "id" ? "opsional, otomatis dibuat dari Hero" : "optional, auto-generated from Hero"}</span>
              </Label>
              <ImageUpload value={entry.share_image_id_url || ""} onChange={(url) => upd({ share_image_id_url: url })} placeholder="Share (ID)" testid={`rmap-share-id-${index}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Share Image (EN)</Label>
              <ImageUpload value={entry.share_image_en_url || ""} onChange={(url) => upd({ share_image_en_url: url })} placeholder="Share (EN)" testid={`rmap-share-en-${index}`} />
            </div>
          </div>

          <ExtrasEditor extras={entry.extras || {}} lists={lists} onChange={(extras) => upd({ extras })} index={index} lang={lang} />
        </div>
      )}
    </div>
  );
}


const MAX_LEARN_MORE_LISTS = 6;

// Per-test headings for the Learn More lists; every character card fills the same lists.
function LearnMoreListsEditor({ lists, onChange, lang }) {
  const id = lang === "id";
  const upd = (i, patch) => onChange(lists.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const add = () => onChange([...lists, { key: `list_${Math.random().toString(36).slice(2, 8)}`, label_id: "", label_en: "" }]);
  return (
    <div className="p-4 rounded-xl border border-border bg-cream-50/60 space-y-3" data-testid="learn-more-lists">
      <div>
        <div className="font-display text-sm">{id ? "Judul daftar di Pelajari Lebih Lanjut" : "Learn More list headings"}</div>
        <p className="text-[11px] text-muted-foreground">
          {id
            ? "Berlaku untuk semua character card di tes ini. Isi daftarnya di tiap card. Daftar tanpa isi tidak ditampilkan."
            : "Shared by every character card in this test. Fill the items in each card. Lists with no items are hidden."}
        </p>
      </div>
      {lists.map((l, i) => (
        <div key={l.key} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`learn-more-list-${i}`}>
          <Input value={l.label_id || ""} onChange={(e) => upd(i, { label_id: e.target.value })} placeholder={id ? "Judul (ID)" : "Heading (ID)"} maxLength={80} className="h-9" data-testid={`learn-more-list-id-${i}`} />
          <Input value={l.label_en || ""} onChange={(e) => upd(i, { label_en: e.target.value })} placeholder={id ? "Judul (EN)" : "Heading (EN)"} maxLength={80} className="h-9" data-testid={`learn-more-list-en-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => onChange(lists.filter((_, k) => k !== i))} className="text-destructive h-9 w-9" data-testid={`learn-more-list-rm-${i}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      {lists.length < MAX_LEARN_MORE_LISTS && (
        <Button size="sm" variant="outline" onClick={add} className="rounded-full" data-testid="learn-more-list-add">
          <Plus className="w-3 h-3 mr-1" /> {id ? "Tambah daftar" : "Add list"}
        </Button>
      )}
    </div>
  );
}

function ExtrasEditor({ extras, lists, onChange, index, lang }) {
  const set = (field, value) => onChange({ ...extras, [field]: value });
  if (!lists.length) return null;
  return (
    <div className="space-y-3 pt-2 border-t border-border" data-testid={`rmap-extras-${index}`}>
      <div>
        <div className="font-display text-sm">{lang === "id" ? "Daftar di Pelajari Lebih Lanjut (opsional)" : "Learn More lists (optional)"}</div>
        <p className="text-[11px] text-muted-foreground">
          {lang === "id" ? "Satu poin per baris. Bagian yang kosong tidak ditampilkan." : "One item per line. Empty lists are hidden."}
        </p>
      </div>
      {lists.map((field) => (
        <div key={field.key} className="grid md:grid-cols-2 gap-3">
          {["id", "en"].map((lg) => (
            <div key={lg}>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                {(lang === "id" ? field.label_id || field.label_en : field.label_en || field.label_id) || (lang === "id" ? "Daftar tanpa judul" : "Untitled list")} ({lg.toUpperCase()})
              </Label>
              <Textarea
                rows={3}
                value={extras[`${field.key}_${lg}`] || ""}
                onChange={(e) => set(`${field.key}_${lg}`, e.target.value)}
                data-testid={`rmap-extra-${field.key}-${lg}-${index}`}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


// ---------- Layout Toggles ----------
export function LayoutTogglesEditor({ layout, onChange, lang }) {
  const toggles = [
    { key: "show_hero", id: "Hero Card", en: "Hero Card", hint_id: "Kartu utama dengan gambar & judul hasil.", hint_en: "Main card with image & result title." },
    { key: "show_reflection", id: "Refleksi", en: "Reflection", hint_id: "Teks refleksi panjang dari Result Mapping.", hint_en: "Reflection text from the Result Mapping." },
    { key: "show_graph", id: "Grafik", en: "Chart", hint_id: "Radar / bar chart per dimensi.", hint_en: "Radar / bar chart per dimension." },
    { key: "show_ranking", id: "Ranking", en: "Ranking", hint_id: "Daftar dimensi diurutkan tinggi ke rendah.", hint_en: "Ordered list high → low." },
    { key: "show_dimensions", id: "Detail Dimensi", en: "Dimension Details", hint_id: "Kartu penjelasan per dimensi.", hint_en: "Explanation card per dimension." },
    { key: "show_combined", id: "Skor Gabungan", en: "Combined Scores", hint_id: "Kartu skor gabungan (formula).", hint_en: "Combined formula-based score cards." },
  ];

  return (
    <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="result-layout-block">
      <div>
        <div className="font-display text-lg">{lang === "id" ? "Layout Halaman Hasil" : "Result Page Layout"}</div>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          {lang === "id"
            ? "Nyalakan / matikan setiap bagian pada halaman hasil publik. Berlaku hanya untuk instrumen ini."
            : "Toggle each section on the public result page. Applies to this instrument only."}
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {toggles.map((t) => (
          <label key={t.key} className="flex items-start gap-3 p-3 rounded-xl border border-border bg-secondary/20 cursor-pointer" data-testid={`layout-toggle-${t.key}`}>
            <Switch checked={!!layout[t.key]} onCheckedChange={(v) => onChange({ ...layout, [t.key]: v })} data-testid={`layout-switch-${t.key}`} />
            <div className="min-w-0">
              <div className="font-display text-sm">{lang === "id" ? t.id : t.en}</div>
              <div className="text-[11px] text-muted-foreground leading-snug">{lang === "id" ? t.hint_id : t.hint_en}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}


// ---------- Result Key Resolver ----------
export function ResultKeyResolverEditor({ resolver, onChange, method, combinedDims, dimensions, criteria, resultMapping, lang }) {
  const set = (patch) => onChange({ ...resolver, ...patch });
  const showModeContinuous = method === "continuous_multi";
  const showTopN = method === "ranking";
  const showCombined = method === "combined_dimension";
  if (!showModeContinuous && !showTopN && !showCombined) return null;

  return (
    <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="result-resolver-block">
      <div>
        <div className="font-display text-lg">{lang === "id" ? "Resolver Result Key" : "Result Key Resolver"}</div>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          {lang === "id"
            ? "Bagaimana sistem memutuskan Hero Card mana yang tampil untuk metode skoring ini."
            : "How the system decides which Hero Card to show for this scoring method."}
        </p>
      </div>

      {showModeContinuous && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Mode" : "Mode"}</Label>
          <div className="grid md:grid-cols-3 gap-2">
            {[
              { v: "generic", id: "Hero Generik", en: "Generic Hero", hint_id: "Satu Hero Card + grafik radar semua dimensi.", hint_en: "Single Hero + radar chart for all dimensions." },
              { v: "signature", id: "Signature Kombinasi", en: "Combination Signature", hint_id: "Hero unik untuk kombinasi tinggi/sedang/rendah per dimensi.", hint_en: "Unique Hero per HIGH/MED/LOW combination." },
              { v: "top1", id: "Top-1 (Dominan)", en: "Top-1 (Dominant)", hint_id: "Hero dari dimensi tertinggi.", hint_en: "Hero of the top dimension." },
              { v: "holland_code", id: "Kode Holland (RIASEC)", en: "Holland Code (RIASEC)", hint_id: "Hero dari 2-3 huruf awal dimensi tertinggi, mis. IAS → IA → I.", hint_en: "Hero from the initials of the top 2-3 dimensions, e.g. IAS → IA → I." },
              { v: "big_five", id: "Big Five (Trait Menonjol)", en: "Big Five (Standout Trait)", hint_id: "Hero dari trait yang paling jauh dari tengah, tinggi atau rendah, mis. E-HIGH atau N-LOW.", hint_en: "Hero from the trait furthest from the middle, high or low, e.g. E-HIGH or N-LOW." },
              { v: "type_code", id: "Kode Tipe 4 Huruf (16 Tipe)", en: "4-Letter Type Code (16 Types)", hint_id: "Hero dari huruf kutub tiap dimensi, mis. INFJ. Satu dimensi = satu pasang huruf.", hint_en: "Hero from each dimension's pole letter, e.g. INFJ. One dimension = one letter pair." },
              { v: "grouped_combination", id: "Kombinasi Grup Dimensi", en: "Grouped Dimension Combination", hint_id: "Kelompokkan kategori stabil lalu cocokkan kombinasi ke result key.", hint_en: "Group stable categories, then map each combination to a result key." },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => set(opt.v === "big_five" && resolver.mode !== "big_five" ? { mode: opt.v, top_n: 1 } : { mode: opt.v })}
                className={`text-left p-3 rounded-xl border-2 transition ${resolver.mode === opt.v ? "border-terracotta-500 bg-terracotta-50" : "border-border bg-card hover:border-terracotta-300"}`}
                data-testid={`resolver-mode-${opt.v}`}
              >
                <div className={`font-display text-sm ${resolver.mode === opt.v ? "text-terracotta-700" : ""}`}>{lang === "id" ? opt.id : opt.en}</div>
                <div className="text-[11px] text-muted-foreground leading-snug mt-1">{lang === "id" ? opt.hint_id : opt.hint_en}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {showModeContinuous && resolver.mode === "grouped_combination" && (
        <GroupedCombinationEditor
          resolver={resolver}
          onChange={onChange}
          dimensions={dimensions || []}
          criteria={criteria || []}
          resultMapping={resultMapping || []}
          lang={lang}
        />
      )}

      {showModeContinuous && resolver.mode === "holland_code" && (
        <HollandResolverSettings resolver={resolver} set={set} lang={lang} />
      )}

      {showModeContinuous && resolver.mode === "type_code" && (
        <TypeCodeResolverSettings resolver={resolver} set={set} dimensions={dimensions || []} lang={lang} />
      )}

      {showModeContinuous && resolver.mode === "big_five" && (
        <BigFiveResolverSettings resolver={resolver} set={set} lang={lang} />
      )}

      {showTopN && (
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Top-N (Ranking)" : "Top-N (Ranking)"}</Label>
          <div className="flex gap-2 mt-2">
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => set({ top_n: n })}
                className={`px-4 py-2 rounded-full border-2 transition text-sm font-display ${resolver.top_n === n ? "border-terracotta-500 bg-terracotta-50 text-terracotta-700" : "border-border bg-card hover:border-terracotta-300"}`}
                data-testid={`resolver-topn-${n}`}
              >Top {n}</button>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">
            {lang === "id"
              ? "Hero Card mencari kombinasi paling spesifik. Contoh Top-3=RIA — kalau tidak ada, fallback ke RI, lalu R, lalu Hero generik."
              : "Hero Card uses the most specific combination. E.g. Top-3=RIA — falls back to RI, then R, then generic."}
          </div>
        </div>
      )}

      {showCombined && (
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Dimensi Kunci" : "Key Dimension"}</Label>
            <select
              value={resolver.combined_key_dim_id || ""}
              onChange={(e) => set({ combined_key_dim_id: e.target.value || null })}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
              data-testid="resolver-combined-key"
            >
              <option value="">{lang === "id" ? "— pilih —" : "— select —"}</option>
              {(combinedDims || []).map((cd) => (
                <option key={cd.id} value={cd.id}>{cd.name_id || cd.name_en || cd.code}</option>
              ))}
            </select>
          </div>
          <CombinedBandsEditor bands={resolver.combined_bands || []} onChange={(b) => set({ combined_bands: b })} lang={lang} />
        </div>
      )}
    </div>
  );
}


function HollandResolverSettings({ resolver, set, lang }) {
  const num = (value) => (value === "" ? null : Math.max(0, Math.min(100, parseFloat(value) || 0)));
  return (
    <div className="space-y-4 border-t border-border pt-5" data-testid="holland-resolver-settings">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Panjang kode untuk Hero Card" : "Code length for the Hero Card"}</Label>
        <div className="flex gap-2 mt-2">
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => set({ top_n: n })}
              className={`px-4 py-2 rounded-full border-2 transition text-sm font-display ${resolver.top_n === n ? "border-terracotta-500 bg-terracotta-50 text-terracotta-700" : "border-border bg-card hover:border-terracotta-300"}`}
              data-testid={`holland-topn-${n}`}
            >{n} {lang === "id" ? "huruf" : n === 1 ? "letter" : "letters"}</button>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          {lang === "id"
            ? "Kode 3 huruf tetap tampil di halaman hasil. Hero Card dicari dari yang paling spesifik: IAS, lalu IA, lalu I, lalu “all”. Skor yang sama diurutkan mengikuti urutan dimensi (R-I-A-S-E-C)."
            : "The 3-letter code is always shown on the result page. The Hero Card is matched from most specific: IAS, then IA, then I, then “all”. Equal scores follow the dimension order (R-I-A-S-E-C)."}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Profil datar (FLAT): selisih di bawah %" : "Flat profile (FLAT): spread below %"}</Label>
          <Input type="number" min={0} max={100} value={resolver.flat_threshold ?? ""} placeholder="10" onChange={(e) => set({ flat_threshold: num(e.target.value) })} data-testid="holland-flat-threshold" />
          <div className="text-[11px] text-muted-foreground mt-1">{lang === "id" ? "Selisih skor tertinggi dan terendah. Pakai result key FLAT." : "Gap between highest and lowest score. Uses result key FLAT."}</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Semua rendah (LOW): skor tertinggi di bawah %" : "All low (LOW): top score below %"}</Label>
          <Input type="number" min={0} max={100} value={resolver.low_threshold ?? ""} placeholder="35" onChange={(e) => set({ low_threshold: num(e.target.value) })} data-testid="holland-low-threshold" />
          <div className="text-[11px] text-muted-foreground mt-1">{lang === "id" ? "Pakai result key LOW. Tanpa mapping FLAT/LOW, hasil kembali ke kode huruf." : "Uses result key LOW. Without a FLAT/LOW mapping the letter code is used."}</div>
        </div>
      </div>
    </div>
  );
}


function TypeCodeResolverSettings({ resolver, set, dimensions, lang }) {
  const tie = resolver.tie_pole === "high" ? "high" : "low";
  const missing = dimensions.filter((d) => !d.pole_low_code || !d.pole_high_code);
  const pairs = dimensions.map((d) => `${d.pole_low_code || "?"}/${d.pole_high_code || "?"}`).join(" · ");
  return (
    <div className="space-y-4 border-t border-border pt-5" data-testid="type-code-resolver-settings">
      <div className="text-[11px] text-muted-foreground leading-relaxed space-y-1.5">
        {lang === "id" ? (
          <>
            <p>Setiap dimensi adalah satu skala preferensi dengan huruf di kedua ujung (isi di tab Dimensi). Skor di atas 50% memilih huruf tinggi, di bawah 50% huruf rendah. Huruf digabung sesuai urutan dimensi, mis. E/I + S/N + T/F + J/P → <span className="font-mono">INFJ</span>.</p>
            <p>Item reverse dan soal pilihan A/B (skor 0 dan 1) sama-sama bisa dipakai. Hero Card dicari dari kodenya, lalu “all”.</p>
          </>
        ) : (
          <>
            <p>Each dimension is one preference scale with a letter at each end (set in the Dimensions tab). Above 50% picks the high letter, below 50% the low letter. Letters join in dimension order, e.g. E/I + S/N + T/F + J/P → <span className="font-mono">INFJ</span>.</p>
            <p>Reverse-keyed items and A/B choice questions (scores 0 and 1) both work. The Hero Card is matched by the code, then “all”.</p>
          </>
        )}
      </div>
      <div className="text-xs" data-testid="type-code-pairs">
        <span className="text-muted-foreground">{lang === "id" ? "Pasangan huruf sekarang: " : "Current letter pairs: "}</span>
        <span className="font-mono">{pairs || "—"}</span>
        {missing.length > 0 && (
          <div className="mt-1 text-destructive" data-testid="type-code-missing">
            {lang === "id"
              ? `Huruf kutub belum lengkap di: ${missing.map((d) => d.code || d.name_id || "?").join(", ")}`
              : `Pole letters missing on: ${missing.map((d) => d.code || d.name_en || "?").join(", ")}`}
          </div>
        )}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Kalau skor tepat 50%" : "When a score is exactly 50%"}</Label>
        <div className="flex gap-2 mt-2">
          {[
            ["low", lang === "id" ? "Pakai huruf rendah" : "Use the low letter"],
            ["high", lang === "id" ? "Pakai huruf tinggi" : "Use the high letter"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => set({ tie_pole: value })}
              className={`px-4 py-2 rounded-full border-2 transition text-sm font-display ${tie === value ? "border-terracotta-500 bg-terracotta-50 text-terracotta-700" : "border-border bg-card hover:border-terracotta-300"}`}
              data-testid={`type-code-tie-${value}`}
            >{label}</button>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          {lang === "id" ? "Jumlah item genap bisa menghasilkan skor seri. Jumlah item ganjil per dimensi menghindarinya." : "An even number of items can produce a tie. An odd number of items per dimension avoids it."}
        </div>
      </div>
    </div>
  );
}


function BigFiveResolverSettings({ resolver, set, lang }) {
  const topN = resolver.top_n === 2 ? 2 : 1;
  return (
    <div className="space-y-4 border-t border-border pt-5" data-testid="big-five-resolver-settings">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Trait untuk Hero Card" : "Traits for the Hero Card"}</Label>
        <div className="flex gap-2 mt-2">
          {[1, 2].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => set({ top_n: n })}
              className={`px-4 py-2 rounded-full border-2 transition text-sm font-display ${topN === n ? "border-terracotta-500 bg-terracotta-50 text-terracotta-700" : "border-border bg-card hover:border-terracotta-300"}`}
              data-testid={`big-five-topn-${n}`}
            >{n} trait</button>
          ))}
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground leading-relaxed space-y-1.5">
        {lang === "id" ? (
          <>
            <p>Trait yang menonjol adalah trait di rentang terendah atau tertinggi (lihat Interpretasi per Dimensi di tab Skoring) yang paling jauh dari 50%. Tanpa rentang, sistem memakai Rendah ≤40% dan Tinggi &gt;60%.</p>
            <p>Result key memakai kode dimensi: <span className="font-mono">E-HIGH</span>, <span className="font-mono">N-LOW</span>, dst. Dengan 2 trait, Hero Card dicari dari <span className="font-mono">E-HIGH_N-LOW</span>, lalu <span className="font-mono">E-HIGH</span>, lalu <span className="font-mono">E</span>, lalu “all”. Kalau semua trait ada di rentang tengah, result key-nya <span className="font-mono">BALANCED</span>.</p>
          </>
        ) : (
          <>
            <p>A standout trait is one in its lowest or highest range (see Per-Dimension Interpretation in the Scoring tab) that sits furthest from 50%. Without ranges the system uses Low ≤40% and High &gt;60%.</p>
            <p>Result keys use the dimension code: <span className="font-mono">E-HIGH</span>, <span className="font-mono">N-LOW</span>, and so on. With 2 traits the Hero Card is matched from <span className="font-mono">E-HIGH_N-LOW</span>, then <span className="font-mono">E-HIGH</span>, then <span className="font-mono">E</span>, then “all”. When every trait is in its middle range the result key is <span className="font-mono">BALANCED</span>.</p>
          </>
        )}
      </div>
    </div>
  );
}


function GroupedCombinationEditor({ resolver, onChange, dimensions, criteria, resultMapping, lang }) {
  const groupings = resolver.dimension_groupings || [];
  const rules = resolver.combination_rules || [];
  const mappingKeys = (resultMapping || []).map((entry) => entry.result_key).filter(Boolean);
  const criteriaByDimension = new Map((criteria || []).map((criterion) => [criterion.dimension_id, criterion]));
  const setGroupings = (next) => onChange({ ...resolver, dimension_groupings: next });
  const setRules = (next) => onChange({ ...resolver, combination_rules: next });

  const toggleDimension = (dimension) => {
    const exists = groupings.some((grouping) => grouping.dimension_id === dimension.id);
    setGroupings(exists
      ? groupings.filter((grouping) => grouping.dimension_id !== dimension.id)
      : [...groupings, { dimension_id: dimension.id, dimension_code: dimension.code, groups: [] }]);
  };
  const updateGrouping = (index, patch) => setGroupings(groupings.map((grouping, i) => i === index ? { ...grouping, ...patch } : grouping));
  const addGroup = (index) => {
    const grouping = groupings[index];
    updateGrouping(index, { groups: [...(grouping.groups || []), { code: "", source_categories: [] }] });
  };
  const updateGroup = (groupingIndex, groupIndex, patch) => {
    const groups = (groupings[groupingIndex].groups || []).map((group, i) => i === groupIndex ? { ...group, ...patch } : group);
    updateGrouping(groupingIndex, { groups });
  };
  const removeGroup = (groupingIndex, groupIndex) => updateGrouping(groupingIndex, {
    groups: (groupings[groupingIndex].groups || []).filter((_, i) => i !== groupIndex),
  });
  const generateRules = () => {
    const existing = new Map(rules.map((rule) => [JSON.stringify(Object.entries(rule.when || {}).sort()), rule.result_key]));
    setRules(buildCombinationRows(groupings).map((rule) => ({
      ...rule,
      result_key: existing.get(JSON.stringify(Object.entries(rule.when).sort())) || "",
    })));
  };
  const validationErrors = validateGroupedCombination({ dimensions, scoring: { criteria }, result_key_resolver: resolver });
  const missingMappings = [...new Set(rules.map((rule) => rule.result_key).filter((key) => key && !mappingKeys.includes(key)))];

  return (
    <div className="space-y-5 border-t border-border pt-5" data-testid="grouped-combination-editor">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "A. Dimensi" : "A. Dimensions"}</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {dimensions.map((dimension) => (
            <label key={dimension.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
              <input type="checkbox" checked={groupings.some((grouping) => grouping.dimension_id === dimension.id)} onChange={() => toggleDimension(dimension)} />
              {dimension.name_id || dimension.name_en || dimension.code}
              <span className="font-mono text-[10px] text-muted-foreground">{dimension.code}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "B. Grup Internal" : "B. Internal Groups"}</Label>
        {groupings.map((grouping, groupingIndex) => {
          const dimension = dimensions.find((item) => item.id === grouping.dimension_id);
          const categories = (criteriaByDimension.get(grouping.dimension_id)?.ranges || []).map((range) => range.code).filter(Boolean);
          return (
            <div key={grouping.dimension_id || groupingIndex} className="rounded-xl border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-display">{dimension?.name_id || dimension?.name_en || grouping.dimension_code}</div>
                <Button type="button" size="sm" variant="outline" onClick={() => addGroup(groupingIndex)}><Plus className="w-3 h-3 mr-1" />{lang === "id" ? "Grup" : "Group"}</Button>
              </div>
              {(grouping.groups || []).map((group, groupIndex) => (
                <div key={groupIndex} className="rounded-lg bg-secondary/30 p-3 space-y-2">
                  <div className="flex gap-2">
                    <Input value={group.code || ""} onChange={(event) => updateGroup(groupingIndex, groupIndex, { code: event.target.value.trim().toUpperCase() })} placeholder="LOW" className="font-mono" />
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeGroup(groupingIndex, groupIndex)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category) => {
                      const selected = (group.source_categories || []).includes(category);
                      return <label key={category} className="flex items-center gap-1.5 text-xs rounded border px-2 py-1.5"><input type="checkbox" checked={selected} onChange={() => updateGroup(groupingIndex, groupIndex, { source_categories: selected ? group.source_categories.filter((code) => code !== category) : [...(group.source_categories || []), category] })} />{category}</label>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "C. Aturan Kombinasi" : "C. Combination Rules"}</Label>
          <Button type="button" size="sm" variant="outline" onClick={generateRules}>{lang === "id" ? "Buat Semua Kombinasi" : "Generate All Combinations"}</Button>
        </div>
        <datalist id="grouped-result-keys">{mappingKeys.map((key) => <option key={key} value={key} />)}</datalist>
        {rules.map((rule, index) => (
          <div key={index} className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-center rounded-lg border p-3">
            <div className="text-xs font-mono">{Object.entries(rule.when || {}).map(([code, group]) => `${code}=${group}`).join(" + ") || "—"}</div>
            <Input list="grouped-result-keys" value={rule.result_key || ""} onChange={(event) => setRules(rules.map((item, i) => i === index ? { ...item, result_key: event.target.value } : item))} placeholder="result_key" className="font-mono" />
            <Button type="button" size="icon" variant="ghost" onClick={() => setRules(rules.filter((_, i) => i !== index))} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      {validationErrors.length > 0 && <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive space-y-1">{validationErrors.map((error) => <div key={error}>• {error}</div>)}</div>}
      {missingMappings.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">{lang === "id" ? "Peringatan: Result Mapping belum ada untuk" : "Warning: Result Mapping is missing for"}: {missingMappings.join(", ")}</div>}
    </div>
  );
}


function CombinedBandsEditor({ bands, onChange, lang }) {
  const upd = (i, patch) => onChange(bands.map((b, k) => (k === i ? { ...b, ...patch } : b)));
  const rm = (i) => onChange(bands.filter((_, k) => k !== i));
  const add = () => onChange([...bands, { code: "", min: 0, max: 100 }]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Band Persentase" : "Percentage Bands"}</Label>
        <Button size="sm" variant="outline" onClick={add} className="rounded-full h-7 text-xs" data-testid="resolver-combined-band-add"><Plus className="w-3 h-3 mr-1" />{lang === "id" ? "Band" : "Band"}</Button>
      </div>
      {bands.length === 0 && (
        <div className="text-[11px] text-muted-foreground italic">
          {lang === "id" ? "Kosong = pakai default (low 0-33 / moderate 34-66 / high 67-100)." : "Empty = default bands (low 0-33 / moderate 34-66 / high 67-100)."}
        </div>
      )}
      <div className="space-y-2">
        {bands.map((b, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`resolver-combined-band-${i}`}>
            <Input value={b.code || ""} onChange={(e) => upd(i, { code: e.target.value })} placeholder={lang === "id" ? "kode (mis. moderate)" : "code (e.g. moderate)"} className="col-span-4 h-9 font-mono text-xs" />
            <Input type="number" value={b.min ?? 0} onChange={(e) => upd(i, { min: parseFloat(e.target.value) || 0 })} placeholder="min%" className="col-span-3 h-9" />
            <Input type="number" value={b.max ?? 100} onChange={(e) => upd(i, { max: parseFloat(e.target.value) || 100 })} placeholder="max%" className="col-span-3 h-9" />
            <Button size="icon" variant="ghost" onClick={() => rm(i)} className="col-span-2 text-destructive h-9"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------- Helpers ----------
function getSuggestedKeys({ method, dimensions, combinedDims, severityLevels }) {
  const out = [];
  if (method === "total_score") {
    for (const s of severityLevels || []) if (s.code) out.push(s.code);
  }
  if (method === "dominant_dimension" || method === "ranking" || method === "continuous_multi") {
    for (const d of dimensions || []) if (d.code) out.push(d.code);
  }
  if (method === "combined_dimension") {
    for (const cd of combinedDims || []) if (cd.code) out.push(cd.code);
    // Add default band codes
    out.push("low", "moderate", "high");
  }
  out.push("all");
  return Array.from(new Set(out));
}


export function getHollandSuggestedKeys(dimensions) {
  const letters = Array.from(new Set((dimensions || []).map((d) => String(d.code || "").trim().charAt(0).toUpperCase()).filter(Boolean)));
  const pairs = [];
  for (const a of letters) for (const b of letters) if (a !== b) pairs.push(`${a}${b}`);
  return [...pairs, "FLAT", "LOW", "all"];
}


export function getTypeCodeSuggestedKeys(dimensions) {
  const pairs = (dimensions || [])
    .map((d) => [d.pole_low_code, d.pole_high_code].map((c) => String(c || "").trim().toUpperCase()))
    .filter(([low, high]) => low && high);
  if (!pairs.length || pairs.length !== (dimensions || []).length || pairs.length > 5) return ["all"];
  let codes = [""];
  for (const [low, high] of pairs) codes = codes.flatMap((code) => [code + low, code + high]);
  return [...codes, "all"];
}


export function getBigFiveSuggestedKeys(dimensions) {
  const codes = Array.from(new Set((dimensions || []).map((d) => String(d.code || "").trim().toUpperCase()).filter(Boolean)));
  return [...codes.flatMap((code) => [`${code}-HIGH`, `${code}-LOW`]), "BALANCED", "all"];
}


function cryptoRandomId() {
  try {
    return (crypto?.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
  } catch { return Math.random().toString(36).slice(2); }
}

